import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiagnosticsPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [error, setError] = useState(null);

  const fetchDiagnostics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/diagnostics/assignment-state`, {
        headers: getAuthHeader()
      });
      setDiagnostics(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnostics();
  }, []);

  if (loading && !diagnostics) {
    return <div className="p-8">Loading diagnostics...</div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-red-600 flex items-center gap-2">
              <AlertCircle />
              Error: {error}
            </div>
            <Button onClick={fetchDiagnostics} className="mt-4">
              <RefreshCw size={16} /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">System Diagnostics</h1>
        <Button onClick={fetchDiagnostics} disabled={loading}>
          <RefreshCw size={16} /> Refresh
        </Button>
      </div>

      {diagnostics && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{diagnostics.total_rules}</div>
                <div className="text-xs text-muted-foreground">
                  {diagnostics.active_rules} active
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{diagnostics.total_users}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{diagnostics.total_leads}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Environment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{diagnostics.environment || 'Unknown'}</div>
              </CardContent>
            </Card>
          </div>

          {/* Rules with Issues */}
          {diagnostics.rules_with_non_sales_users && diagnostics.rules_with_non_sales_users.length > 0 && (
            <Card className="mb-6 border-red-500">
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <AlertCircle />
                  ⚠️ Rules with Non-Sales Users ({diagnostics.rules_with_non_sales_users.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {diagnostics.rules_with_non_sales_users.map((rule, idx) => (
                  <div key={idx} className="mb-4 p-4 bg-red-50 rounded">
                    <div className="font-semibold">{rule.rule_name}</div>
                    <div className="text-sm text-gray-600">ID: {rule.rule_id}</div>
                    <div className="mt-2">
                      <strong>Invalid Users:</strong>
                      <ul className="list-disc ml-5">
                        {rule.invalid_users.map((u, i) => (
                          <li key={i}>
                            {u.username} - <span className="text-red-600">Role: {u.role}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Assignment Counts */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Assignment Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">User</th>
                    <th className="text-right p-2">Lead Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(diagnostics.assignment_counts || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([user, count]) => (
                      <tr key={user} className="border-b">
                        <td className="p-2">{user}</td>
                        <td className="text-right p-2">{count}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Recent Leads */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Recent Leads (Last 20)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">ID</th>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Assigned To</th>
                      <th className="text-left p-2">Source</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostics.recent_leads?.map((lead) => (
                      <tr key={lead.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{lead.id}</td>
                        <td className="p-2">{lead.name}</td>
                        <td className="p-2">{lead.assigned_to || 'UNASSIGNED'}</td>
                        <td className="p-2">{lead.source || 'N/A'}</td>
                        <td className="p-2">{lead.assignment_status || 'N/A'}</td>
                        <td className="p-2">
                          {lead.created_at ? new Date(lead.created_at).toLocaleString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* All Rules */}
          <Card>
            <CardHeader>
              <CardTitle>All Assignment Rules</CardTitle>
            </CardHeader>
            <CardContent>
              {diagnostics.all_rules?.map((rule) => (
                <div key={rule.id} className="mb-4 p-4 border rounded">
                  <div className="flex justify-between">
                    <div>
                      <div className="font-semibold">{rule.name}</div>
                      <div className="text-sm text-gray-600">
                        Type: {rule.assignment_type} | Priority: {rule.priority}
                      </div>
                    </div>
                    <div>
                      {rule.is_active ? (
                        <span className="text-green-600 font-semibold">ACTIVE</span>
                      ) : (
                        <span className="text-gray-400">INACTIVE</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <strong>Assigned Users:</strong> {rule.assign_to_users?.join(', ') || 'None'}
                  </div>
                  {rule.conditions && rule.conditions.length > 0 && (
                    <div className="mt-2 text-sm">
                      <strong>Conditions:</strong>
                      <ul className="list-disc ml-5">
                        {rule.conditions.map((c, i) => (
                          <li key={i}>
                            {c.field} {c.operator} "{c.value}"
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DiagnosticsPage;
